<script lang="ts">
  import { xhr, toast, getVideoThumb } from "$app/utils"
  import Tags from "svelte-tags-input"
  import Honeybadger from "$app/utils/honeybadger"
  import Switch from "$app/components/Switch.svelte"

  let props = $props()

  let categories: string[] = $state(props.categories || [])
  let statuses: string[] = props.statuses || []
  let status = props.status || ""
  let video: VideoType | {} = props.video || {}
  let oncreated: (video: VideoType) => void = props.oncreated
  let onupdated: (video: VideoType) => void = props.onupdated

  let errors: any = $state({})

  const initialForm = {
    title: "",
    url: "",
    source: "youtube",
    thumbnail: null,
    status: "",
    categories: [],
    description: "",
    public: false
  }

  let thumbnailUrl: any = $state("")

  function setThumbnailUrl(): void {
    if (form.url !== video.url) {
      thumbnailUrl = getVideoThumb(form.url, form.source)
    }
  }

  let form = { ...initialForm }

  const fields = [
    "title",
    "url",
    "source",
    "status",
    "public",
    "categories",
    "description"
  ]

  function getFormData(): FormData {
    const formData = new FormData()
    fields.forEach((field) => {
      if (["categories"].includes(field)) {
        form[field].forEach((value) => {
          formData.append(`video[${field}][]`, value)
        })
      } else {
        formData.append(`video[${field}]`, form[field] || "")
      }
    })

    if (thumbnail) {
      formData.append("video[thumbnail]", thumbnail)
    } else if (thumbnailUrl) {
      formData.append("video[thumbnail_source_url]", thumbnailUrl)
    }

    return formData
  }

  // Save New Video
  let saving = $state(false)

  async function saveVideo(): Promise<void> {
    if (status === "edit") {
      await updateVideo()
    } else {
      await createVideo()
    }
  }

  async function updateVideo(): Promise<void> {
    saving = true
    errors = {}

    try {
      const resp = await xhr.patch(`/admin/videos/${video.id}`, getFormData(), {
        headers: {
          "Content-Type": "multipart/form-data"
        }
      })

      onupdated(resp.data)
      toast("The video has been Added.", "success")
    } catch (e) {
      toast("There was an error saving the new video", "error")
      console.error(e)
      if (e.response && e.response.data) {
        errors = { ...errors, ...e.response.data.errors }
      } else {
        if (typeof Honeybadger.notify !== "undefined") {
          Honeybadger.notify(e)
        }
      }
    } finally {
      saving = false
    }
  }

  async function createVideo(): Promise<void> {
    saving = true
    errors = {}

    try {
      const resp = await xhr.post("/admin/videos", getFormData(), {
        headers: {
          "Content-Type": "multipart/form-data"
        }
      })

      oncreated(resp.data)
      toast("The video has been Added.", "success")
    } catch (e: any) {
      toast("There was an error saving the new video", "error")
      if (e.response && e.response.data) {
        errors = { ...errors, ...e.response.data.errors }
      } else {
        if (typeof Honeybadger.notify !== "undefined") {
          Honeybadger.notify(e)
        }
      }
    } finally {
      saving = false
    }
  }

  let thumbnail: any, thumbnailInput: HTMLInputElement

  function onFileSelected(e: any): void {
    thumbnail = e.target.files[0]

    let reader = new FileReader()
    reader.readAsDataURL(thumbnail)
    reader.onload = (e) => {
      thumbnailUrl = e.target.result
    }
  }

  function setForm(): void {
    fields.forEach((fld) => {
      if (fld === "categories") {
        form[fld] = video[fld] || []
      } else {
        form[fld] = video[fld] || ""
      }
    })
  }

  if (status) {
    setForm()
    thumbnailUrl = video.thumbnail_url || ""
  }
</script>

<div class="mt-3 sm:mt-5 overflow-visible w-full">
  <h2 class="text-base font-semibold leading-6 text-gray-900">
    {status === "edit" ? "Edit" : "New"} Video
  </h2>

  <div class="mt-4">
    <div class="my-2 px-2">
      <div class="grid grid-cols-1 gap-2">
        <div class="input-cont">
          <label for="">Title</label>

          <input
            id="video_title"
            type="text"
            bind:value={form.title}
            class="input-admin w-full input"
          />
          <div class="text-red-500">{errors.title || ""}</div>
        </div>

        <div class="input-cont">
          <label for="">Source</label>

          <select
            id="video_source"
            bind:value={form.source}
            onchange={setThumbnailUrl}
            class="input-admin"
          >
            <option value="youtube">YouTube</option>
            <option value="vimeo">Vimeo</option>
          </select>
          <div class="text-red-500">{errors.source || ""}</div>
        </div>

        <div class="input-cont">
          <label for="">Link</label>

          <input
            id="video_url"
            type="text"
            bind:value={form.url}
            class="input-admin w-full input"
            onblur={setThumbnailUrl}
          />
          <div class="text-red-500">{errors.url || ""}</div>
        </div>

        <div class="flex gap-8 py-2">
          <div class="flex gap-2">
            <label for="">Status</label>
            <select bind:value={form.status} class="input-admin w-48">
              {#each statuses as st}
                <option value={st}>{st}</option>
              {/each}
            </select>
            <div class="text-red-500">
              {errors.status || ""}
            </div>
          </div>
          <div class="flex gap-2 items-center">
            <Switch active={form.public} />
            <button
              onclick={() => (form.public = !form.public)}
              class="cursor-pointer"
              class:text-red-500={!form.public}
              class:text-green-500={form.public}
            >
              {form.public ? "Public" : "Private"}
            </button>
          </div>
        </div>

        <div>
          <label for="">Thumbnail Image</label>

          <div class="relative" style="width:333px;height:250px">
            {#if thumbnailUrl}
              <img
                src={thumbnailUrl}
                id="thumbnail-image"
                alt="Video thumb"
                class="w-auto bg-gray-300 shadow-lg flex-shrink-0 cursor-pointer"
              />
            {:else}
              <div class="bg-gray-400 h-full text-center text-2xl p-4">
                No image thumb
              </div>
            {/if}
            <button
              class="absolute top-0 left-0 p-4 opacity-0 hover:opacity-70 bg-slate-800 w-full h-full text-center cursor-pointer"
              onclick={() => thumbnailInput.click()}
            >
              <span class="text-2xl font-semibold">
                Click to replace video url
              </span>
            </button>
          </div>

          <input
            type="file"
            class="hidden"
            bind:this={thumbnailInput}
            required
            onchange={onFileSelected}
            accept=".jpg, .jpeg, .png"
          />
          <div class="text-red-500">{errors.thumbnail || ""}</div>
        </div>

        <div class="input-cont text-gray-700">
          <label for="">Categories</label>

          <div class="svelte-tags">
            <Tags
              bind:tags={form.categories}
              autoComplete={categories}
              addKeys={[]}
              minChars={0}
              removeKeys={[8]}
              onlyUnique={true}
            />
          </div>
          <div class="text-red-500">{errors.categories || ""}</div>
        </div>

        <div class="input-cont">
          <label for="">Description / Notes</label>

          <textarea
            bind:value={form.description}
            rows="3"
            class="input-admin shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
          ></textarea>
          <div class="text-red-500">{errors.description || ""}</div>
        </div>

        <div class="flex items-center justify-center">
          <button
            class="btn btn-rounded success btn-big relative"
            disabled={saving}
            onclick={() => {
              saveVideo()
            }}
          >
            {status === "new" ? "Create" : "Update"} Video

            <div
              class="absolute top-0 left-0 w-full p-4 bg-slate-700 opacity-60"
              class:hidden={!saving}
            >
              Saving...
            </div>
          </button>
        </div>
      </div>
    </div>
  </div>
</div>
